/**
 * Client-side validation against the very schemas the worker validates with,
 * so a form can say what is wrong before anything is sent.
 */
import type { ZodType } from 'zod';

export type FieldErrors = Record<string, string>;

export type ValidationResult<T> =
  | { ok: true; data: T }
  | { ok: false; errors: FieldErrors; message: string };

/** First error per top-level field, keyed by field name. */
export function validate<T>(schema: ZodType<T>, value: unknown): ValidationResult<T> {
  const result = schema.safeParse(value);
  if (result.success) return { ok: true, data: result.data };

  const errors: FieldErrors = {};
  for (const issue of result.error.issues) {
    const key = issue.path.length ? String(issue.path[0]) : '_';
    if (!errors[key]) errors[key] = humanise(issue.message, key);
  }
  const first = Object.values(errors)[0] ?? 'Check the form and try again.';
  return { ok: false, errors, message: first };
}

/**
 * Zod's default messages are written for developers ("Too small: expected
 * string to have >=1 characters"). Rewrite the ones a person can hit.
 */
function humanise(message: string, field: string): string {
  const name = FIELD_NAMES[field] ?? field;
  if (/expected string to have >=1|too_small|Too small/i.test(message)) return `${name} is required.`;
  if (/Invalid url|Invalid URL/i.test(message)) return 'That does not look like a web address.';
  if (/Invalid input: expected number/i.test(message)) return `${name} must be a number.`;
  if (/regex|Invalid string/i.test(message)) return `${name} is not in a valid format.`;
  if (/Too big|expected string to have <=/i.test(message)) return `${name} is too long.`;
  return message;
}

const FIELD_NAMES: Record<string, string> = {
  title: 'Name',
  name: 'Name',
  month: 'Month',
  day: 'Day',
  year: 'Year',
  url: 'Feed address',
  colour: 'Colour',
  refreshHours: 'Refresh interval',
};
